import { useState, useMemo, useCallback } from "react"
import axios from "axios"
import { RiCheckLine, RiCloseLine } from "@remixicon/react"
import AdminSectionsWrapper, { adminSectionHeader } from "../../AdminUtils/Dashboard.admin"
import "./AddAdmin.scss"

const AddAdmin = () => {
    
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [repeatPassword, setRepeatPassword] = useState("")
  const [errors, setErrors] = useState({ email: "", password: "", repeatPassword: "" })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState("")

  const validation = useMemo(() => {
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      passwordsMatch: password === repeatPassword && password.length > 0,
    }
  }, [password, repeatPassword])

  const isFormValid = useMemo(() => {
    return (
      email.trim() !== "" &&
      validation.minLength &&
      validation.passwordsMatch &&
      Object.values(errors).every((err) => err === "")
    )
  }, [email, validation, errors])

  const validateEmail = useCallback((value) => {
    if (!value.trim()) return "Email is required"
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(value)) return "Please enter a valid email address"
    return ""
  }, [])

  const validatePassword = useCallback((value) => {
    if (!value) return "Password is required"
    if (value.length < 8) return "Password must be at least 8 characters long"
    return ""
  }, [])

  const validateRepeatPassword = useCallback(
    (value) => {
      if (!value) return "Please repeat your password"
      if (value !== password) return "Passwords do not match"
      return ""
    },
    [password],
  )

  const handleEmailChange = (e) => {
    const value = e.target.value
    setEmail(value)
    setErrors((prev) => ({ ...prev, email: validateEmail(value) }))
  }

  const handlePasswordChange = (e) => {
    const value = e.target.value
    setPassword(value)
    setErrors((prev) => ({ ...prev, password: validatePassword(value) }))
  }

  const handleRepeatPasswordChange = (e) => {
    const value = e.target.value
    setRepeatPassword(value)
    setErrors((prev) => ({ ...prev, repeatPassword: validateRepeatPassword(value) }))
  }

  const handleReset = useCallback(() => {
    setEmail("")
    setPassword("")
    setRepeatPassword("")
    setErrors({ email: "", password: "", repeatPassword: "" })
    setSubmitMessage("")
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!isFormValid) return

    setIsSubmitting(true)
    setSubmitMessage("")
    const api = process.env.REACT_APP_API_URL
    try{ 
      await axios.post(
        `${api}/adm/signup`,
        {
          email,
          password,
        },
        {
          withCredentials: true,
        },
      )

      setSubmitMessage("Admin account created successfully!")
      handleReset()
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setSubmitMessage(error.response?.data?.message || "Failed to create admin account. Please try again.")
      } else {
        setSubmitMessage("An unexpected error occurred. Please try again.")
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="admin-container">
      <div className="admin-wrapper">
        {adminSectionHeader("Add New Admin","Create a new administrator account with access to the dashboard" )}

        <form className="admin-form-card" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">
              Email Address <span className="required">*</span>
            </label>
            <input
              className={`form-input ${errors.email ? "error" : ""}`}
              type="email"
              placeholder="admin@example.com"
              value={email}
              onChange={handleEmailChange}
              disabled={isSubmitting}
            />
            {errors.email && (
              <div className="error-message">
                <RiCloseLine />
                {errors.email}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              Password <span className="required">*</span>
            </label>
            <input
              className={`form-input ${errors.password ? "error" : ""}`}
              type="password"
              placeholder="Enter a secure password"
              value={password}
              onChange={handlePasswordChange}
              disabled={isSubmitting}
            />
            {errors.password && (
              <div className="error-message">
                <RiCloseLine />
                {errors.password}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              Repeat Password <span className="required">*</span>
            </label>
            <input
              className={`form-input ${errors.repeatPassword ? "error" : ""}`}
              type="password"
              placeholder="Repeat the password"
              value={repeatPassword}
              onChange={handleRepeatPasswordChange}
              disabled={isSubmitting}
            />
            {errors.repeatPassword && (
              <div className="error-message">
                <RiCloseLine />
                {errors.repeatPassword}
              </div>
            )}
          </div>

          <div className="requirements-section">
            <div className="requirements-title">Password Requirements:</div>
            <div className="requirement-item">
              <span className={`requirement-icon ${validation.minLength ? "met" : "unmet"}`}>
                {validation.minLength ? <RiCheckLine /> : <RiCloseLine />}
              </span>
              <span>At least 8 characters long</span>
            </div>
            <div className="requirement-item">
              <span className={`requirement-icon ${validation.hasUppercase ? "met" : "unmet"}`}>
                {validation.hasUppercase ? <RiCheckLine /> : <RiCloseLine />}
              </span>
              <span>Contains uppercase letter (recommended)</span>
            </div>
            <div className="requirement-item">
              <span className={`requirement-icon ${validation.hasNumber ? "met" : "unmet"}`}>
                {validation.hasNumber ? <RiCheckLine /> : <RiCloseLine />}
              </span>
              <span>Contains number (recommended)</span>
            </div>
          </div>

          {submitMessage && (
            <div className="error-message" style={{ marginBottom: "1rem", marginTop: "1rem", color: "inherit" }}>
              {submitMessage}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={handleReset} disabled={isSubmitting}>
              Reset Form
            </button>
            <button type="submit" className="btn-primary" disabled={!isFormValid || isSubmitting}>
              {isSubmitting ? "Adding..." : "Add Admin"}
            </button>
          </div>
        </form>

        <div className="info-section">
          <div className="info-title">Important Information</div>
          <p className="info-text">
            New administrators will have full access to the dashboard. Make sure to share login credentials securely and
            encourage them to change their password on first login.
          </p>
        </div>
      </div>
    </div>
  )
}

const AddAdminWrapper = () => {
    return <AdminSectionsWrapper>
            <AddAdmin/>
        </AdminSectionsWrapper>
}


export default AddAdminWrapper
